<script lang="ts">
import "./MTabs.scss";
import { NOOP } from "@vue/shared";
import {
  computed,
  defineComponent,
  h,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  watchEffect,
} from "vue";
import { isArray, isString } from "vue-composable";
import { useRouter } from "vue-router";

// ENG-21320 native QTabs/QTab/QTabPanels replacement (tablist + indicator + overflow arrows + keyboard nav, mounts only active MTab); explicit `name` keeps MTabs in the parent-chain (task-list.vue parentNames check).
export default defineComponent({
  name: "MTabs",
  inheritAttrs: false,
  props: {
    modelValue: [String, Object],
    panelClass: String,
    noFlex: Boolean,
    fit: Boolean,
    noPadding: Boolean,

    scroll: Boolean,
    allowOverflow: Boolean,

    routerSync: [Boolean, String],

    onBeforeRouteChange: Function,
    gutter: Boolean,
    bgGrey: Boolean,
    bgWhite: Boolean,
  },

  emits: ["update:modelValue", "scroll"],

  setup(props, { emit, slots, attrs }) {
    inject("prevent-overflow", NOOP)(!props.allowOverflow);
    inject("remove-padding", NOOP)(true);

    const value = ref(props.modelValue);

    const defaultValue = ref("");

    watch(
      () => props.modelValue,
      (v) => (value.value = v),
    );

    const router = useRouter();
    const queryName = computed(() =>
      props.routerSync ? (isString(props.routerSync) ? props.routerSync : "tab") : undefined,
    );

    watch([value, defaultValue], ([v, d]) => {
      if (queryName.value && v !== router.currentRoute.value.query[queryName.value]) {
        if (!d) return;
        let query: Record<string, string> = {
          [queryName.value]: v === defaultValue.value ? undefined : (v as any),
        };

        if (props.onBeforeRouteChange) {
          const r = props.onBeforeRouteChange(v, query);
          if (r) {
            query = r;
          }
        }

        // eslint-disable-next-line no-undef
        if (IS_APP) {
          router.push({
            query,
          });
        }
      }
    });

    watchEffect(() => {
      if (queryName.value) {
        const v = router.currentRoute.value.query[queryName.value];
        if (v) {
          value.value = v;
        } else if (defaultValue.value) {
          value.value = defaultValue.value;
        }
      }
    });

    const activate = (name: string) => {
      value.value = name;
      emit("update:modelValue", name);
    };

    // --- overflow-arrow / scroll state (was provided by QTabs) ---
    const barContentRef = ref<HTMLElement>();
    const leftArrow = ref(false);
    const rightArrow = ref(false);
    let resizeObserver: ResizeObserver | undefined;

    const updateArrows = () => {
      const el = barContentRef.value;
      if (!el) {
        leftArrow.value = false;
        rightArrow.value = false;
        return;
      }
      leftArrow.value = el.scrollLeft > 0;
      rightArrow.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;
    };

    const scrollByArrow = (direction: number) => {
      const el = barContentRef.value;
      if (!el) return;
      el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: "smooth" });
    };

    // ENG-21320 press-and-hold to scroll continuously (was QTabs behaviour); a quick
    // tap under the hold threshold still does the click jump above. Speed is
    // time-based (px/second) so it is consistent across display refresh rates.
    const HOLD_DELAY = 250;
    const HOLD_SPEED = 1200; // px per second
    let holdDelayTimer = 0;
    let holdRaf = 0;
    let lastFrame = 0;
    let didHold = false;

    const stopHold = () => {
      window.clearTimeout(holdDelayTimer);
      if (holdRaf) cancelAnimationFrame(holdRaf);
      holdRaf = 0;
      lastFrame = 0;
      // restore CSS smooth scrolling (forced to auto during a hold, below)
      if (barContentRef.value) barContentRef.value.style.scrollBehavior = "";
      window.removeEventListener("pointerup", stopHold);
      window.removeEventListener("pointercancel", stopHold);
    };

    const startHold = (direction: number) => (event: PointerEvent) => {
      if (event.button !== 0) return; // primary button / touch only
      didHold = false;
      window.addEventListener("pointerup", stopHold);
      window.addEventListener("pointercancel", stopHold);
      holdDelayTimer = window.setTimeout(() => {
        const el = barContentRef.value;
        if (!el) return;
        didHold = true;
        // The bar has scroll-behavior:smooth; a fresh smooth animation each frame
        // never completes, so force instant scrolling for the duration of the hold.
        el.style.scrollBehavior = "auto";
        const step = (now: number) => {
          if (!barContentRef.value) return;
          const dt = lastFrame ? (now - lastFrame) / 1000 : 0;
          lastFrame = now;
          barContentRef.value.scrollLeft += direction * HOLD_SPEED * dt;
          holdRaf = requestAnimationFrame(step);
        };
        holdRaf = requestAnimationFrame(step);
      }, HOLD_DELAY);
    };

    const onArrowClick = (direction: number) => {
      if (didHold) {
        didHold = false; // hold already scrolled — swallow the trailing click
        return;
      }
      scrollByArrow(direction);
    };

    const scrollActiveIntoView = () => {
      const el = barContentRef.value;
      if (!el) return;
      const all = el.querySelectorAll<HTMLElement>(".m-tabs__tab");
      const active = el.querySelector<HTMLElement>(".m-tabs__tab--active");
      if (active) {
        if (active === all[0]) {
          // first tab: snap fully left so the left arrow clears
          el.scrollTo({ left: 0, behavior: "smooth" });
        } else if (active === all[all.length - 1]) {
          // last tab: snap fully right so the right arrow clears
          el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: "smooth" });
        } else {
          const left = active.offsetLeft;
          const right = left + active.offsetWidth;
          if (left < el.scrollLeft) {
            el.scrollTo({ left, behavior: "smooth" });
          } else if (right > el.scrollLeft + el.clientWidth) {
            el.scrollTo({ left: right - el.clientWidth, behavior: "smooth" });
          }
        }
      }
      updateArrows();
    };

    onMounted(() => {
      const el = barContentRef.value;
      if (el) {
        el.addEventListener("scroll", updateArrows, { passive: true });
        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => updateArrows());
          resizeObserver.observe(el);
        }
      }
      nextTick(scrollActiveIntoView);
    });

    onBeforeUnmount(() => {
      barContentRef.value?.removeEventListener("scroll", updateArrows);
      resizeObserver?.disconnect();
      stopHold();
    });

    watch(value, () => {
      nextTick(scrollActiveIntoView);
    });

    return () => {
      const defaultSlot = slots.default?.();
      const children = defaultSlot?.map((x) => (x.key === "_default" ? x.children : x)).flat() as {
        children?: any[];
        props?: { name: string };
      }[];

      const tabs: any[] = [];

      if (children) {
        for (const child of children) {
          if (!child) continue;

          if (isArray(child.children)) {
            tabs.push(...child.children.filter((x) => x?.props?.name));
          } else if (child?.props?.name) {
            tabs.push(child);
          }
        }
      }

      if (!value.value && tabs.length) {
        //@ts-ignore
        value.value = tabs[0].props?.name;
        emit("update:modelValue", value.value);
      }
      if (tabs.length) {
        defaultValue.value = tabs[0].props?.name;
      }

      const tabNames: string[] = tabs.map((t: any) => t.props?.name);

      const onTabKeydown = (e: KeyboardEvent) => {
        const current = tabNames.indexOf(value.value as string);
        let next = current;
        switch (e.key) {
          case "ArrowRight":
          case "ArrowDown":
            next = (current + 1) % tabNames.length;
            break;
          case "ArrowLeft":
          case "ArrowUp":
            next = (current - 1 + tabNames.length) % tabNames.length;
            break;
          case "Home":
            next = 0;
            break;
          case "End":
            next = tabNames.length - 1;
            break;
          case "Enter":
          case " ":
            activate((e.currentTarget as HTMLElement)?.dataset.name ?? (value.value as string));
            return;
          default:
            return;
        }
        e.preventDefault();
        const name = tabNames[next];
        activate(name);
        nextTick(() => {
          barContentRef.value
            ?.querySelector<HTMLElement>(`.m-tabs__tab[data-name="${CSS.escape(name)}"]`)
            ?.focus();
        });
      };

      // Panels — only the active MTab is mounted, wrapped in the tabpanel div.
      const activeTab = tabs.find((t: any) => t.props?.name === value.value);
      const panels = [
        h(
          "div",
          {
            class: [{ "flex-auto": !props.noFlex }, "m-tabs__panels", props.panelClass],
            style: {
              backgroundColor: "inherit",
            },
          },
          activeTab
            ? [
                h("div", { class: "m-tabs__panel", role: "tabpanel", key: value.value }, [
                  activeTab,
                ]),
              ]
            : [],
        ),
      ];

      const tabButtons = tabs.map((t: any) => {
        const name = t.props?.name;
        const isActive = name === value.value;
        const headerSlot = slots["header:" + name];
        return h(
          "div",
          {
            class: ["m-tabs__tab", isActive ? "m-tabs__tab--active" : "m-tabs__tab--inactive"],
            role: "tab",
            "aria-selected": isActive ? "true" : "false",
            tabindex: isActive ? 0 : -1,
            "data-name": name,
            key: name,
            onClick: () => activate(name),
            onKeydown: onTabKeydown,
          },
          [
            headerSlot ? headerSlot() : h("div", { class: "m-tabs__tab-label" }, t.props?.label),
            h("div", { class: "m-tabs__tab-indicator" }),
          ],
        );
      });

      const arrowSvg = (dir: "left" | "right") =>
        h(
          "svg",
          {
            viewBox: "0 0 24 24",
            width: "24",
            height: "24",
            fill: "currentColor",
            "aria-hidden": "true",
          },
          [
            h("path", {
              d:
                dir === "left"
                  ? "M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                  : "M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z",
            }),
          ],
        );

      const tabHeader = h("div", { class: "m-tabs__bar", role: "tablist" }, [
        h("div", { class: "m-tabs__bar-content", ref: barContentRef }, tabButtons),
        h(
          "i",
          {
            class: [
              "m-tabs__arrow",
              "m-tabs__arrow--left",
              { "m-tabs__arrow--faded": !leftArrow.value },
            ],
            "aria-hidden": "true",
            role: "presentation",
            onPointerdown: startHold(-1),
            onClick: () => onArrowClick(-1),
          },
          [arrowSvg("left")],
        ),
        h(
          "i",
          {
            class: [
              "m-tabs__arrow",
              "m-tabs__arrow--right",
              { "m-tabs__arrow--faded": !rightArrow.value },
            ],
            "aria-hidden": "true",
            role: "presentation",
            onPointerdown: startHold(1),
            onClick: () => onArrowClick(1),
          },
          [arrowSvg("right")],
        ),
      ]);

      const headNodes = slots.head ? slots.head() : [];

      return h(
        "div",
        {
          class: [
            {
              "tab-fit": props.fit,
              "no-padding": props.noPadding,
              "flex-col m-tabs flex-auto": true,
              "overflow-hidden": !props.scroll,
              gutter: props.gutter,
              "m-tabs--bg-grey": props.bgGrey,
              "m-tabs--bg-white": props.bgWhite,
            },
            attrs.class,
          ],
          style: attrs.style,
        },
        [
          headNodes.length
            ? h(
                "div",
                {
                  style: {
                    display: "grid",
                    gap: "0.5rem",
                    gridTemplateColumns: `minmax(0, 1fr)  ${headNodes.map(() => "auto")} 0px`,
                    alignItems: "center",
                  },
                },
                // note adding extra div have an extra margin on the right
                [h("div", [tabHeader]), ...headNodes, h("div")],
              )
            : tabHeader,

          props.scroll
            ? h(
                "div",
                {
                  class: "scroll-area m-mb-md",

                  onScroll(e: any) {
                    emit("scroll", e);
                  },
                },
                panels,
              )
            : panels,
        ],
      );
    };
  },
});
</script>
